import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import AdminPastries from '../components/AdminPastries';
import AdminNews from '../components/AdminNews';
import AdminGallery from '../components/AdminGallery';
import AdminOrders from '../components/AdminOrders';
import AdminSettings from '../components/AdminSettings';
import AdminMenu from '../components/AdminMenu';
import {
    LayoutDashboard,
    Package,
    ShoppingBag,
    Newspaper,
    Image as ImageIcon,
    Settings,
    Users,
    ChevronRight,
    LogOut,
    ArrowLeft,
    Utensils
} from 'lucide-react';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState({
        users: 0,
        pastries: 0,
        souvenirs: 0,
        news: 0,
        photos: 0,
        orders: 0,
        menuProducts: 0
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchStats();
    }, []);

    // Smooth scroll to top on tab change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeTab]);

    const fetchStats = async () => {
        try {
            const fetchCount = async (collection, filter = "") => {
                try {
                    const result = await pb.collection(collection).getList(1, 1, {
                        skipTotal: false,
                        filter: filter
                    });
                    return result.totalItems;
                } catch (e) { return 0; }
            };

            const usersCount = await fetchCount('users');
            const pastriesCount = await fetchCount('patisseries', 'estSouvenir = false');
            const souvenirsCount = await fetchCount('patisseries', 'estSouvenir = true');
            const newsCount = await fetchCount('actualites');
            const photosCount = await fetchCount('galerie');
            const ordersCount = await fetchCount('commandes', 'status="pending"');
            const menuProductsCount = await fetchCount('produits_carte');

            setStats({
                users: usersCount,
                pastries: pastriesCount,
                souvenirs: souvenirsCount,
                news: newsCount,
                photos: photosCount,
                orders: ordersCount,
                menuProducts: menuProductsCount
            });
        } catch (err) {
            console.error("Error fetching admin stats:", err);
        }
    };

    const handleLogout = () => {
        pb.authStore.clear();
        window.location.href = '/';
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className="tab-pane">
                        <div className="admin-stats-grid">
                            <div className="admin-premium-card" onClick={() => setActiveTab('orders')}>
                                <div className="card-icon"><Package size={24} /></div>
                                <h3>Commandes</h3>
                                <div className="admin-stat-value" style={{ color: stats.orders > 0 ? '#e74c3c' : 'inherit' }}>
                                    {stats.orders}
                                </div>
                                <p>en attente</p>
                            </div>

                            <div className="admin-premium-card">
                                <div className="card-icon"><Users size={24} /></div>
                                <h3>Clients</h3>
                                <div className="admin-stat-value">{stats.users}</div>
                                <p>inscrits</p>
                            </div>

                            <div className="admin-premium-card" onClick={() => setActiveTab('pastries')}>
                                <div className="card-icon"><ShoppingBag size={24} /></div>
                                <h3>Catalogue</h3>
                                <div className="admin-stat-value">{stats.pastries}</div>
                                <p>produits actifs</p>
                            </div>

                            <div className="admin-premium-card" onClick={() => setActiveTab('pastries')}>
                                <div className="card-icon" style={{ color: 'var(--color-gold)' }}><ImageIcon size={24} /></div>
                                <h3>Souvenirs</h3>
                                <div className="admin-stat-value">{stats.souvenirs}</div>
                                <p>créations archivées</p>
                            </div>

                            <div className="admin-premium-card" onClick={() => setActiveTab('news')}>
                                <div className="card-icon"><Newspaper size={24} /></div>
                                <h3>Articles</h3>
                                <div className="admin-stat-value">{stats.news}</div>
                                <p>actualités</p>
                            </div>
                            <div className="admin-premium-card" onClick={() => setActiveTab('menu')}>
                                <div className="card-icon"><Utensils size={24} /></div>
                                <h3>La Carte</h3>
                                <div className="admin-stat-value">{stats.menuProducts}</div>
                                <p>en boutique</p>
                            </div>
                        </div>

                        <div className="admin-quick-actions-section">
                            <h2>Actions Rapides</h2>
                            <div className="quick-actions-grid">
                                <button className="quick-action-button" onClick={() => setActiveTab('orders')}>
                                    <Package size={20} /> Gérer les commandes <ChevronRight size={16} />
                                </button>
                                <button className="quick-action-button" onClick={() => setActiveTab('pastries')}>
                                    <ShoppingBag size={20} /> Modifier les pâtisseries <ChevronRight size={16} />
                                </button>
                                <button className="quick-action-button" onClick={() => setActiveTab('settings')}>
                                    <Settings size={20} /> Configuration site <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'orders': return <AdminOrders />;
            case 'pastries': return <AdminPastries />;
            case 'news': return <AdminNews />;
            case 'gallery': return <AdminGallery />;
            case 'menu': return <AdminMenu />;
            case 'settings': return <AdminSettings />;
            default: return null;
        }
    };

    return (
        <div className="admin-dashboard-page">
            <div className="admin-container">
                <header className="admin-header-premium">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center' }}>
                        <a href="/" className="back-link"><ArrowLeft size={20} /></a>
                        <div>
                            <span className="admin-subtitle">Console de Gestion</span>
                            <h1>Bonjour, {pb.authStore.model?.name || 'Administrateur'}</h1>
                        </div>
                    </div>
                    <div style={{ margin: '20px auto', height: '1px', background: '#eee', maxWidth: '400px' }}></div>
                </header>

                <nav className="admin-horizontal-tabs">
                    <button className={`horizontal-tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                        <LayoutDashboard size={18} /> Vue d'ensemble
                    </button>
                    <button className={`horizontal-tab-btn ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
                        <Package size={18} /> Commandes {stats.orders > 0 && <span className="tab-badge">{stats.orders}</span>}
                    </button>
                    <button className={`horizontal-tab-btn ${activeTab === 'pastries' ? 'active' : ''}`} onClick={() => setActiveTab('pastries')}>
                        <ShoppingBag size={18} /> Produits
                    </button>
                    <button className={`horizontal-tab-btn ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => setActiveTab('menu')}>
                        <Utensils size={18} /> La Carte
                    </button>
                    <button className={`horizontal-tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                        <Settings size={18} /> Paramètres
                    </button>
                </nav>

                <div className="admin-layout">
                    <aside className="admin-sidebar">
                        <nav className="admin-tabs">
                            <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                                <LayoutDashboard size={18} /> <span>Aperçu</span>
                            </button>
                            <button className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
                                <Package size={18} /> <span>Commandes</span>
                            </button>
                            <button className={`tab-btn ${activeTab === 'pastries' ? 'active' : ''}`} onClick={() => setActiveTab('pastries')}>
                                <ShoppingBag size={18} /> <span>Produits</span>
                            </button>
                            <button className={`tab-btn ${activeTab === 'news' ? 'active' : ''}`} onClick={() => setActiveTab('news')}>
                                <Newspaper size={18} /> <span>Actualités</span>
                            </button>
                            <button className={`tab-btn ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => setActiveTab('menu')}>
                                <Utensils size={18} /> <span>La Carte</span>
                            </button>
                            <button className={`tab-btn ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')}>
                                <ImageIcon size={18} /> <span>Galerie</span>
                            </button>
                            <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                                <Settings size={18} /> <span>Paramètres</span>
                            </button>

                            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                                <button className="tab-btn logout-btn-admin" onClick={handleLogout} style={{ color: '#e74c3c' }}>
                                    <LogOut size={18} /> <span>Quitter</span>
                                </button>
                            </div>
                        </nav>
                    </aside>

                    <main className="admin-content">
                        {renderTabContent()}
                    </main>
                </div>
            </div>

            {/* Admin Mobile Bottom Navigation */}
            <nav className="mobile-bottom-nav">
                <button className={`mobile-nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                    <LayoutDashboard size={22} />
                    <span>Aperçu</span>
                </button>
                <button className={`mobile-nav-item ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
                    <Package size={22} />
                    <span>Commandes</span>
                </button>
                <button className={`mobile-nav-item ${activeTab === 'pastries' ? 'active' : ''}`} onClick={() => setActiveTab('pastries')}>
                    <ShoppingBag size={22} />
                    <span>Produits</span>
                </button>
                <button className={`mobile-nav-item ${activeTab === 'news' ? 'active' : ''}`} onClick={() => setActiveTab('news')}>
                    <Newspaper size={22} />
                    <span>Articles</span>
                </button>
                <button className={`mobile-nav-item ${activeTab === 'menu' ? 'active' : ''}`} onClick={() => setActiveTab('menu')}>
                    <Utensils size={22} />
                    <span>Carte</span>
                </button>
                <button className={`mobile-nav-item ${activeTab === 'gallery' ? 'active' : ''}`} onClick={() => setActiveTab('gallery')}>
                    <ImageIcon size={22} />
                    <span>Photos</span>
                </button>
                <button className={`mobile-nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                    <Settings size={22} />
                    <span>Config</span>
                </button>
            </nav>
        </div>
    );
};

export default AdminDashboard;
